import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  useCreateCapabilityModel,
  useUpdateCapabilityModel,
} from '../../services/capability-model.service';
import { useLLMModels } from '../../services/llm-catalog.service';
import {
  DEFAULT_MODEL_COST_TIER,
  MODEL_COST_TIER_LABELS,
  MODEL_COST_TIERS,
} from '../../common/model-tiers';
import { CapabilityModel, CapabilityModelFormValues, LLMModel } from '../../types/admin';
import {
  formatCatalogModelOption,
  formatModelPriceLabel,
  formatOutputModalities,
  getCatalogModelOptionParts,
} from '../../utils/llmCatalogModelDisplay';
import { VideoModelConfigFields } from '../../features/admin/generate-video/components/VideoModelConfigFields';
import { ParamOverridesFields } from './ParamOverridesFields';

interface CapabilityModelFormDialogProps {
  open: boolean;
  onClose: () => void;
  capabilityPublicId: string;
  model?: CapabilityModel | null;
  /** Task types the capability accepts; the catalog dropdown only lists models serving one of them. */
  compatibleTaskTypeCodes?: string[];
  /** Models already mapped to this capability; used to suggest the next priority per cost tier. */
  existingModels?: CapabilityModel[];
  /** Show the Generate Video per-model settings (edits `config.video`). */
  showVideoSettings?: boolean;
}

type ErrorKey = keyof CapabilityModelFormValues | 'catalogModel';

const emptyValues: CapabilityModelFormValues = {
  llmModelPublicId: '',
  providerCode: '',
  modelCode: '',
  costTier: DEFAULT_MODEL_COST_TIER,
  priority: 100,
  isDefault: false,
  isActive: true,
  config: '',
};

export const CapabilityModelFormDialog: React.FC<CapabilityModelFormDialogProps> = ({
  open,
  onClose,
  capabilityPublicId,
  model,
  compatibleTaskTypeCodes = [],
  existingModels = [],
  showVideoSettings = false,
}) => {
  const isEditMode = !!model;
  const createMutation = useCreateCapabilityModel();
  const updateMutation = useUpdateCapabilityModel();
  const { data: catalogModels = [], isLoading: isLoadingCatalog } = useLLMModels({
    isActive: true,
    all: true,
  });

  const usableCatalogModels = useMemo(
    () =>
      catalogModels.filter(
        (item) =>
          (item.usageScope === 'user' || item.usageScope === 'both') &&
          (item.publicId === model?.llmModelPublicId ||
            isTaskTypeCompatible(item, compatibleTaskTypeCodes))
      ),
    [catalogModels, compatibleTaskTypeCodes, model?.llmModelPublicId]
  );

  const [useCatalogModel, setUseCatalogModel] = useState(true);
  const [formValues, setFormValues] = useState<CapabilityModelFormValues>(emptyValues);
  const [numberDisplays, setNumberDisplays] = useState({
    priority: String(emptyValues.priority),
  });
  const [errors, setErrors] = useState<Partial<Record<ErrorKey, string>>>({});

  const getNextPriority = (costTier: CapabilityModelFormValues['costTier']) =>
    getNextTierPriority(existingModels, costTier);

  useEffect(() => {
    if (model) {
      const nextValues = {
        llmModelPublicId: model.llmModelPublicId ?? '',
        providerCode: model.providerCode,
        modelCode: model.modelCode,
        costTier: model.costTier,
        priority: model.priority,
        isDefault: model.isDefault,
        isActive: model.isActive,
        config: model.config ?? '',
        paramOverrides: model.paramOverrides ?? null,
      };
      setUseCatalogModel(!!model.llmModelPublicId);
      setFormValues(nextValues);
      setNumberDisplays({
        priority: String(nextValues.priority),
      });
    } else {
      const nextPriority = getNextPriority(emptyValues.costTier);
      setUseCatalogModel(true);
      setFormValues({ ...emptyValues, priority: nextPriority });
      setNumberDisplays({
        priority: String(nextPriority),
      });
    }
    setErrors({});
  }, [model, open]);

  const selectedCatalogModel = useMemo(
    () => usableCatalogModels.find((item) => item.publicId === formValues.llmModelPublicId),
    [formValues.llmModelPublicId, usableCatalogModels]
  );

  const validateForm = () => {
    const nextErrors: Partial<Record<ErrorKey, string>> = {};

    if (useCatalogModel && !formValues.llmModelPublicId) {
      nextErrors.catalogModel = 'Select a catalog model';
    }

    if (!useCatalogModel && !formValues.modelCode.trim()) {
      nextErrors.modelCode = 'Model code is required';
    }

    if (!useCatalogModel && !formValues.providerCode.trim()) {
      nextErrors.providerCode = 'Provider code is required';
    }

    if (!Number.isInteger(formValues.priority) || formValues.priority < 1) {
      nextErrors.priority = 'Priority must be at least 1';
    }

    (['config'] as Array<keyof CapabilityModelFormValues>).forEach((field) => {
      const value = formValues[field];
      if (typeof value !== 'string' || !value.trim()) return;

      try {
        const parsed = JSON.parse(value);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          nextErrors[field] = 'Must be a valid JSON object';
        }
      } catch {
        nextErrors[field] = 'Must be a valid JSON object';
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      llmModelPublicId: useCatalogModel ? formValues.llmModelPublicId : null,
      providerCode: useCatalogModel
        ? selectedCatalogModel?.provider?.code
        : formValues.providerCode.trim(),
      modelCode: useCatalogModel ? selectedCatalogModel?.code : formValues.modelCode.trim(),
      costTier: formValues.costTier,
      priority: formValues.priority,
      isDefault: formValues.isDefault,
      isActive: formValues.isActive,
      config: normalizeOptionalJsonForSave(formValues.config, isEditMode),
      paramOverrides: normalizeParamOverridesForSave(
        formValues.paramOverrides,
        model?.paramOverrides ?? null
      ),
    };

    if (isEditMode && model) {
      await updateMutation.mutateAsync({
        id: model.id,
        ...payload,
      });
    } else {
      await createMutation.mutateAsync({
        capabilityPublicId,
        ...payload,
        llmModelPublicId: payload.llmModelPublicId ?? undefined,
      });
    }

    onClose();
  };

  const handleValueChange = <K extends keyof CapabilityModelFormValues>(
    field: K,
    value: CapabilityModelFormValues[K]
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCatalogModelChange = (nextModel: LLMModel | null) => {
    setFormValues((prev) => ({ ...prev, llmModelPublicId: nextModel?.publicId ?? '' }));
    if (errors.catalogModel) {
      setErrors((prev) => ({ ...prev, catalogModel: undefined }));
    }
  };

  const handleNumberChange = (field: 'priority', raw: string, max?: number) => {
    if (!/^\d*$/.test(raw)) return;
    setNumberDisplays((prev) => ({ ...prev, [field]: raw }));
    const parsed = Number(raw);
    if (raw && Number.isInteger(parsed)) {
      handleValueChange(field, max ? Math.min(max, parsed) : parsed);
    }
  };

  const handleCostTierChange = (costTier: CapabilityModelFormValues['costTier']) => {
    handleValueChange('costTier', costTier);
    if (isEditMode) return;
    const nextPriority = getNextPriority(costTier);
    handleValueChange('priority', nextPriority);
    setNumberDisplays((prev) => ({ ...prev, priority: String(nextPriority) }));
  };

  const handleNumberBlur = (field: 'priority', min: number, max?: number) => {
    const current = formValues[field];
    const clamped = Math.max(min, max ? Math.min(max, current) : current);
    handleValueChange(field, clamped);
    setNumberDisplays((prev) => ({ ...prev, [field]: String(clamped) }));
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Model Mapping' : 'Add Model Mapping'}</DialogTitle>

      <DialogContent>
        <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormControlLabel
            className="md:col-span-2"
            control={
              <Checkbox
                checked={useCatalogModel}
                onChange={(event) => setUseCatalogModel(event.target.checked)}
                disabled={isLoading}
              />
            }
            label="Use model from LLM Catalog"
          />

          {useCatalogModel ? (
            <Autocomplete
              fullWidth
              className="md:col-span-2"
              options={usableCatalogModels}
              value={selectedCatalogModel ?? null}
              loading={isLoadingCatalog}
              disabled={isLoading || isLoadingCatalog}
              getOptionLabel={formatCatalogModelOption}
              isOptionEqualToValue={(option, value) => option.publicId === value.publicId}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <CatalogModelOptionContent model={option} />
                </Box>
              )}
              openOnFocus
              autoHighlight
              onChange={(_event, nextModel) => handleCatalogModelChange(nextModel)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Catalog Model"
                  error={!!errors.catalogModel}
                  helperText={errors.catalogModel}
                />
              )}
              noOptionsText="No user-facing catalog models found"
            />
          ) : (
            <>
              <TextField
                fullWidth
                label="Provider Code"
                value={formValues.providerCode}
                onChange={(event) => handleValueChange('providerCode', event.target.value)}
                error={!!errors.providerCode}
                helperText={errors.providerCode || 'Example: cloudflare, openai, anthropic'}
                disabled={isLoading}
              />
              <TextField
                fullWidth
                label="Model Code"
                value={formValues.modelCode}
                onChange={(event) => handleValueChange('modelCode', event.target.value)}
                error={!!errors.modelCode}
                helperText={errors.modelCode || 'Provider model id'}
                disabled={isLoading}
              />
            </>
          )}

          {useCatalogModel && selectedCatalogModel && (
            <Box className="grid grid-cols-1 gap-3 rounded border border-slate-200 p-3 md:col-span-2 md:grid-cols-5">
              <ReadOnlyValue label="Provider" value={selectedCatalogModel.provider?.name ?? '-'} />
              <ReadOnlyValue
                label="Provider Code"
                value={selectedCatalogModel.provider?.code ?? '-'}
              />
              <ReadOnlyValue label="Model Code" value={selectedCatalogModel.code} />
              <ReadOnlyValue
                label="Total Price"
                value={formatModelPriceLabel(selectedCatalogModel)}
              />
              <ReadOnlyValue
                label="Output"
                value={formatOutputModalities(
                  selectedCatalogModel.outputModalities,
                  selectedCatalogModel.taskType?.outputModalities
                )}
              />
            </Box>
          )}

          <FormControl fullWidth required>
            <InputLabel>Cost Tier</InputLabel>
            <Select
              value={formValues.costTier}
              onChange={(event) =>
                handleCostTierChange(event.target.value as CapabilityModelFormValues['costTier'])
              }
              label="Cost Tier"
              disabled={isLoading}
            >
              {MODEL_COST_TIERS.map((tier) => (
                <MenuItem key={tier} value={tier}>
                  {MODEL_COST_TIER_LABELS[tier]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Priority"
            value={numberDisplays.priority}
            onChange={(event) => handleNumberChange('priority', event.target.value)}
            onBlur={() => handleNumberBlur('priority', 1)}
            error={!!errors.priority}
            helperText={errors.priority || 'Higher priority is selected first'}
            disabled={isLoading}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            required
          />

          <Box className="flex items-center gap-6">
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.isDefault}
                  onChange={(event) => handleValueChange('isDefault', event.target.checked)}
                  disabled={isLoading}
                />
              }
              label="Default"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.isActive}
                  onChange={(event) => handleValueChange('isActive', event.target.checked)}
                  disabled={isLoading}
                />
              }
              label="Active"
            />
          </Box>

          {showVideoSettings && (
            <VideoModelConfigFields
              key={model?.id ?? 'new'}
              value={formValues.config ?? ''}
              onChange={(value) => handleValueChange('config', value)}
              disabled={isLoading}
            />
          )}

          <ParamOverridesFields
            value={formValues.paramOverrides}
            onChange={(value) => handleValueChange('paramOverrides', value)}
            modelConfig={selectedCatalogModel?.config}
            disabled={isLoading}
          />

          <TextField
            className="md:col-span-2"
            fullWidth
            label="Config Override JSON"
            value={formValues.config ?? ''}
            onChange={(event) => handleValueChange('config', event.target.value)}
            error={!!errors.config}
            helperText={errors.config || 'Optional runtime overrides for this capability'}
            disabled={isLoading}
            multiline
            rows={4}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || isLoadingCatalog}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Max priority within the cost tier + 1 (1 when the tier is empty). */
function getNextTierPriority(
  models: CapabilityModel[],
  costTier: CapabilityModelFormValues['costTier']
) {
  const priorities = models
    .filter((item) => item.costTier === costTier)
    .map((item) => item.priority);
  return priorities.length > 0 ? Math.max(...priorities) + 1 : 1;
}

/** No restriction when the capability declares no task types or the model has none. */
function isTaskTypeCompatible(item: LLMModel, compatibleTaskTypeCodes: string[]) {
  if (compatibleTaskTypeCodes.length === 0) return true;
  const modelCodes = [
    ...(item.taskTypeCodes ?? []),
    ...(item.taskTypeCode ? [item.taskTypeCode] : []),
    ...(item.taskType ? [item.taskType.code] : []),
  ];
  return modelCodes.some((code) => compatibleTaskTypeCodes.includes(code));
}

function normalizeOptionalJsonForSave(value: string | undefined, clearAsNull: boolean) {
  const trimmed = value?.trim();
  if (trimmed) return trimmed;
  return clearAsNull ? null : undefined;
}

function normalizeParamOverridesForSave(
  value: CapabilityModelFormValues['paramOverrides'],
  original: CapabilityModelFormValues['paramOverrides']
) {
  if (JSON.stringify(value ?? null) === JSON.stringify(original ?? null)) return undefined;
  return value && Object.keys(value).length > 0 ? value : null;
}

const ReadOnlyValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" className="text-gray-500">
      {label}
    </Typography>
    <Typography variant="body2" className="break-all font-mono">
      {value}
    </Typography>
  </Box>
);

const CatalogModelOptionContent: React.FC<{ model: LLMModel }> = ({ model }) => {
  const { providerModel, priceLabel, taskTypeLabel } = getCatalogModelOptionParts(model);

  return (
    <Box
      className="grid w-full items-center gap-3 font-mono text-sm"
      sx={{ gridTemplateColumns: 'minmax(220px, 1.6fr) minmax(150px, 1fr) minmax(130px, 1fr)' }}
    >
      <Typography variant="body2" className="truncate font-mono">
        {providerModel}
      </Typography>
      <Typography variant="body2" className="truncate font-mono">
        {priceLabel}
      </Typography>
      <Typography variant="body2" className="truncate font-mono">
        {taskTypeLabel}
      </Typography>
    </Box>
  );
};
